import { lazy, Suspense, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  FileQuestion,
  FileText,
  Pencil,
  Plus,
  Trash2,
  UploadCloud,
} from 'lucide-react';

import { formatSopIdentifier, listSops } from '@/api/sops.api';
import {
  addLesson,
  archiveCourse,
  deleteLesson,
  getCourse,
  publishCourse,
  reorderLessons,
  saveCourseFinalQuiz,
  saveLessonQuiz,
  updateCourse,
  updateLesson,
  type AdminCourseDetail,
  type AdminLesson,
  type AdminQuiz,
  type CourseState,
  type QuizQuestionInput,
} from '@/api/trainingAdmin.api';
import type { LessonContentKind } from '@/api/training.api';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeTrainingError } from '@/lib/training/errors';
import { useToast } from '@/hooks/useToast';
import { TrainingNoAccessCard } from './_chrome';
import { useTrainingAdminGate } from './_gate';

/**
 * Course builder (manage_training / superadmin) — metadata, ordered
 * lessons (rich-text via the lazy TipTap editor, or sop-wrapped via a
 * SOP picker), per-lesson + final quiz editors, publish/archive.
 *
 * The TipTap editor bundle loads on demand: `RichTextEditor` is the only
 * module importing it, pulled in via React.lazy the first time an
 * author opens a rich-text lesson modal.
 */

const RichTextEditor = lazy(() => import('@/components/richtext/RichTextEditor'));

const SELECT_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-2 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const STATE_META: Record<CourseState, { label: string; variant: BadgeProps['variant'] }> = {
  draft: { label: 'Draft', variant: 'info' },
  published: { label: 'Published', variant: 'success' },
  archived: { label: 'Archived', variant: 'neutral' },
};

function genId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export function CourseBuilderPage() {
  const { canManage } = useTrainingAdminGate();
  const { id } = useParams<{ id: string }>();

  const courseQuery = useQuery({
    queryKey: ['training-admin', 'course', id],
    queryFn: () => getCourse(id ?? ''),
    enabled: canManage && Boolean(id),
  });

  if (!canManage) {
    return <TrainingNoAccessCard capability="manage_training" />;
  }

  if (courseQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (courseQuery.error || !courseQuery.data) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div className="flex-1">
          <p className="text-[14px] font-medium text-kw-danger-text">Course not available</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {describeTrainingError(courseQuery.error, 'This course does not exist.')}
          </p>
          <Link
            to="/admin/training/courses"
            className="mt-3 inline-block text-[13px] text-kw-cerulean hover:underline"
          >
            ← All courses
          </Link>
        </div>
      </Card>
    );
  }

  return <Builder course={courseQuery.data} />;
}

type QuizTarget = { kind: 'final' } | { kind: 'lesson'; lesson: AdminLesson & { quiz: AdminQuiz | null } };

function Builder({ course }: { course: AdminCourseDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [lessonModal, setLessonModal] = useState<{
    open: boolean;
    editing: (AdminLesson & { quiz: AdminQuiz | null }) | null;
  }>({ open: false, editing: null });
  const [quizModal, setQuizModal] = useState<QuizTarget | null>(null);
  const [confirm, setConfirm] = useState<'publish' | 'archive' | null>(null);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['training-admin', 'course', course.id] });
    void queryClient.invalidateQueries({ queryKey: ['training-admin', 'courses'] });
  }

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => reorderLessons(course.id, orderedIds),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not reorder the lessons.'), { variant: 'danger' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (lessonId: string) => deleteLesson(lessonId),
    onSuccess: () => {
      invalidate();
      toast.show('Lesson deleted', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not delete the lesson.'), { variant: 'danger' });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => publishCourse(course.id),
    onSuccess: (updated) => {
      invalidate();
      setConfirm(null);
      toast.show(`Course published (v${String(updated.version)})`, { variant: 'success' });
    },
    onError: (err) => {
      setConfirm(null);
      toast.show(describeTrainingError(err, 'Could not publish the course.'), { variant: 'danger' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveCourse(course.id),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
      toast.show('Course archived', { variant: 'success' });
    },
    onError: (err) => {
      setConfirm(null);
      toast.show(describeTrainingError(err, 'Could not archive the course.'), { variant: 'danger' });
    },
  });

  function moveLesson(index: number, delta: -1 | 1): void {
    const ids = course.lessons.map((l) => l.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    const next = [...ids];
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[target] = a;
    reorderMutation.mutate(next);
  }

  const stateMeta = STATE_META[course.state];
  const willBumpVersion = course.publishedAt !== null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <Link
          to="/admin/training/courses"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          All courses
        </Link>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="typo-h1">{course.title}</h1>
          <p className="mt-1 flex items-center gap-2 text-[14px] text-kw-muted">
            <Badge variant={stateMeta.variant}>{stateMeta.label}</Badge>
            <span>v{course.version}</span>
            <span className="font-mono text-[12px]">{course.slug}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {course.state !== 'archived' ? (
            <Button
              variant="secondary"
              onClick={() => {
                setConfirm('archive');
              }}
            >
              <Archive className="size-4" />
              Archive
            </Button>
          ) : null}
          <Button
            onClick={() => {
              setConfirm('publish');
            }}
          >
            <UploadCloud className="size-4" />
            {course.state === 'published' ? 'Re-publish' : 'Publish'}
          </Button>
        </div>
      </div>

      <MetadataCard key={course.updatedAt} course={course} onSaved={invalidate} />

      {/* Lessons */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Lessons</h2>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setLessonModal({ open: true, editing: null });
            }}
          >
            <Plus className="size-3.5" />
            Add lesson
          </Button>
        </div>
        <Card className="p-0">
          {course.lessons.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-kw-muted">
              No lessons yet — a course needs at least one lesson before it can be published.
            </p>
          ) : (
            <ul className="divide-y divide-kw-border">
              {course.lessons.map((lesson, index) => (
                <li key={lesson.id} className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label={`Move ${lesson.title} up`}
                      disabled={index === 0 || reorderMutation.isPending}
                      onClick={() => {
                        moveLesson(index, -1);
                      }}
                      className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
                    >
                      <ArrowUp className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${lesson.title} down`}
                      disabled={index === course.lessons.length - 1 || reorderMutation.isPending}
                      onClick={() => {
                        moveLesson(index, 1);
                      }}
                      className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
                    >
                      <ArrowDown className="size-3.5" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium text-kw-shark">
                      {index + 1}. {lesson.title}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-kw-muted">
                      <FileText className="size-3.5" />
                      {lesson.contentKind === 'sop' ? 'SOP-wrapped lesson' : 'Rich text'}
                      {lesson.estimatedMinutes !== null ? ` · ${String(lesson.estimatedMinutes)} min` : ''}
                      {lesson.quiz ? ` · quiz (${String(lesson.quiz.questions.length)} questions)` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setQuizModal({ kind: 'lesson', lesson });
                    }}
                  >
                    <FileQuestion className="size-3.5" />
                    {lesson.quiz ? 'Edit quiz' : 'Add quiz'}
                  </Button>
                  <button
                    type="button"
                    aria-label={`Edit ${lesson.title}`}
                    onClick={() => {
                      setLessonModal({ open: true, editing: lesson });
                    }}
                    className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  {course.state === 'draft' ? (
                    <button
                      type="button"
                      aria-label={`Delete ${lesson.title}`}
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        deleteMutation.mutate(lesson.id);
                      }}
                      className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* Final quiz */}
      <section className="space-y-2">
        <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Final quiz</h2>
        <Card className="flex flex-wrap items-center gap-3 p-4">
          <FileQuestion className="size-5 shrink-0 text-kw-cerulean" />
          <div className="min-w-0 flex-1">
            {course.finalQuiz ? (
              <>
                <p className="text-[14px] font-medium text-kw-shark">{course.finalQuiz.title}</p>
                <p className="mt-0.5 text-[12px] text-kw-muted">
                  {course.finalQuiz.questions.length} question
                  {course.finalQuiz.questions.length === 1 ? '' : 's'} · pass at{' '}
                  {course.finalQuiz.passThresholdPercent}%
                  {course.finalQuiz.maxAttempts !== null
                    ? ` · ${String(course.finalQuiz.maxAttempts)} attempts max`
                    : ' · unlimited attempts'}
                </p>
              </>
            ) : (
              <p className="text-[13px] text-kw-muted">
                No final quiz — trainees complete the course from lessons alone.
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setQuizModal({ kind: 'final' });
            }}
          >
            {course.finalQuiz ? 'Edit final quiz' : 'Add final quiz'}
          </Button>
        </Card>
      </section>

      {lessonModal.open ? (
        <LessonModal
          courseId={course.id}
          editing={lessonModal.editing}
          onClose={() => {
            setLessonModal({ open: false, editing: null });
          }}
          onSaved={() => {
            invalidate();
            toast.show(lessonModal.editing ? 'Lesson updated' : 'Lesson added', { variant: 'success' });
          }}
        />
      ) : null}

      {quizModal ? (
        <QuizEditorModal
          target={quizModal}
          courseId={course.id}
          defaultThreshold={course.passThresholdPercentDefault}
          existing={quizModal.kind === 'final' ? course.finalQuiz : quizModal.lesson.quiz}
          onClose={() => {
            setQuizModal(null);
          }}
          onSaved={() => {
            invalidate();
            toast.show('Quiz saved', { variant: 'success' });
          }}
        />
      ) : null}

      {confirm === 'publish' ? (
        <Modal
          open
          onClose={() => {
            setConfirm(null);
          }}
          title={course.state === 'published' ? 'Re-publish course' : 'Publish course'}
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              {willBumpVersion
                ? `Re-publishing bumps the course to v${String(course.version + 1)}. New completions and quiz attempts will snapshot the new version.`
                : 'Publishing makes the course visible to enrolled trainees and available for assignment rules.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirm(null);
                }}
              >
                Cancel
              </Button>
              <Button
                isLoading={publishMutation.isPending}
                onClick={() => {
                  publishMutation.mutate();
                }}
              >
                {course.state === 'published' ? 'Re-publish' : 'Publish'}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {confirm === 'archive' ? (
        <Modal
          open
          onClose={() => {
            setConfirm(null);
          }}
          title="Archive course"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              Archiving takes the course out of circulation — trainees stop seeing it and assignments stop
              expanding. Training records are kept.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirm(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={archiveMutation.isPending}
                onClick={() => {
                  archiveMutation.mutate();
                }}
              >
                Archive
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Metadata
// ─────────────────────────────────────────────────────────────────────────

function MetadataCard({ course, onSaved }: { course: AdminCourseDetail; onSaved: () => void }) {
  const toast = useToast();
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description ?? '');
  const [category, setCategory] = useState(course.category ?? '');
  const [passThreshold, setPassThreshold] = useState(course.passThresholdPercentDefault);

  const mutation = useMutation({
    mutationFn: () =>
      updateCourse(course.id, {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        category: category.trim() ? category.trim() : null,
        passThresholdPercentDefault: passThreshold,
      }),
    onSuccess: () => {
      onSaved();
      toast.show('Course details saved', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not save the course details.'), { variant: 'danger' });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!title.trim()) return;
    mutation.mutate();
  }

  return (
    <Card className="p-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Title"
            name="builder-title"
            required
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
          <Input
            label="Category"
            name="builder-category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
            }}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_160px]">
          <Input
            label="Description"
            name="builder-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
          <Input
            label="Default pass %"
            name="builder-pass-threshold"
            type="number"
            min={0}
            max={100}
            required
            value={passThreshold}
            onChange={(e) => {
              setPassThreshold(Number(e.target.value));
            }}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" size="sm" variant="secondary" isLoading={mutation.isPending}>
            Save details
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Lesson create/edit modal
// ─────────────────────────────────────────────────────────────────────────

function LessonModal({
  courseId,
  editing,
  onClose,
  onSaved,
}: {
  courseId: string;
  editing: AdminLesson | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(editing?.title ?? '');
  const [contentKind, setContentKind] = useState<LessonContentKind>(editing?.contentKind ?? 'rich_text');
  const [contentJson, setContentJson] = useState<TipTapDoc | null>(editing?.contentJson ?? null);
  const [sopId, setSopId] = useState(editing?.sopId ?? '');
  const [estimatedMinutes, setEstimatedMinutes] = useState(
    editing?.estimatedMinutes !== null && editing?.estimatedMinutes !== undefined
      ? String(editing.estimatedMinutes)
      : '',
  );
  const [error, setError] = useState<string | null>(null);

  const sopsQuery = useQuery({ queryKey: ['compliance-sops'], queryFn: listSops });

  const mutation = useMutation({
    mutationFn: () => {
      const minutes = estimatedMinutes.trim() === '' ? null : Number(estimatedMinutes);
      const base = {
        title: title.trim(),
        estimatedMinutes: minutes,
      };
      const body =
        contentKind === 'sop'
          ? { ...base, contentKind: 'sop' as const, sopId }
          : {
              ...base,
              contentKind: 'rich_text' as const,
              ...(contentJson ? { contentJson } : {}),
            };
      return editing ? updateLesson(editing.id, body) : addLesson(courseId, body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(describeTrainingError(err, 'Could not save the lesson.'));
    },
  });

  const valid = title.trim().length > 0 && (contentKind !== 'sop' || sopId !== '');

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit lesson' : 'Add lesson'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
          <Input
            label="Title"
            name="lesson-title"
            required
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="e.g. Receiving raw materials"
          />
          <Input
            label="Est. minutes"
            name="lesson-minutes"
            type="number"
            min={1}
            max={6000}
            value={estimatedMinutes}
            onChange={(e) => {
              setEstimatedMinutes(e.target.value);
            }}
          />
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Content type</span>
          <select
            value={contentKind}
            onChange={(e) => {
              setContentKind(e.target.value === 'sop' ? 'sop' : 'rich_text');
            }}
            className={SELECT_CLS}
          >
            <option value="rich_text">Rich text (write it here)</option>
            <option value="sop">Wrap an existing SOP</option>
          </select>
        </label>

        {contentKind === 'sop' ? (
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">SOP</span>
            <select
              value={sopId}
              onChange={(e) => {
                setSopId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">Select an SOP…</option>
              {(sopsQuery.data ?? []).map((sop) => (
                <option key={sop.id} value={sop.id}>
                  {formatSopIdentifier(sop)} — {sop.title}
                </option>
              ))}
            </select>
            <span className="text-[12px] text-kw-muted">
              The SOP needs an approved revision; trainees always see the latest approved content.
            </span>
          </label>
        ) : (
          <div className="space-y-1">
            <span className="text-[13px] font-medium text-kw-label">Content</span>
            <Suspense
              fallback={
                <div className="flex h-[220px] items-center justify-center rounded-kw-md border border-kw-border">
                  <div className="size-6 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
                </div>
              }
            >
              <RichTextEditor value={contentJson} onChange={setContentJson} />
            </Suspense>
          </div>
        )}

        {error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!valid}>
            {editing ? 'Save lesson' : 'Add lesson'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Quiz editor modal — the quiz is edited as a document (replace-set)
// ─────────────────────────────────────────────────────────────────────────

interface QuestionDraft {
  key: string;
  prompt: string;
  options: { id: string; text: string }[];
  correctOptionId: string;
  explanation: string;
}

function newQuestion(): QuestionDraft {
  const a = genId('opt');
  const b = genId('opt');
  return {
    key: genId('q'),
    prompt: '',
    options: [
      { id: a, text: '' },
      { id: b, text: '' },
    ],
    correctOptionId: a,
    explanation: '',
  };
}

function draftsFromQuiz(quiz: AdminQuiz): QuestionDraft[] {
  return quiz.questions.map((q) => ({
    key: q.id,
    prompt: q.prompt,
    options: q.optionsJson.map((o) => ({ id: o.id, text: o.text })),
    correctOptionId: q.correctOptionId,
    explanation: q.explanation ?? '',
  }));
}

function QuizEditorModal({
  target,
  courseId,
  defaultThreshold,
  existing,
  onClose,
  onSaved,
}: {
  target: QuizTarget;
  courseId: string;
  defaultThreshold: number;
  existing: AdminQuiz | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(
    existing?.title ?? (target.kind === 'final' ? 'Final quiz' : `${target.lesson.title} quiz`),
  );
  const [passThreshold, setPassThreshold] = useState(existing?.passThresholdPercent ?? defaultThreshold);
  const [maxAttempts, setMaxAttempts] = useState(
    existing?.maxAttempts !== null && existing?.maxAttempts !== undefined ? String(existing.maxAttempts) : '',
  );
  const [questions, setQuestions] = useState<QuestionDraft[]>(
    existing ? draftsFromQuiz(existing) : [newQuestion()],
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const doc = {
        title: title.trim(),
        passThresholdPercent: passThreshold,
        maxAttempts: maxAttempts.trim() === '' ? null : Number(maxAttempts),
        questions: questions.map(
          (q): QuizQuestionInput => ({
            prompt: q.prompt.trim(),
            options: q.options.map((o) => ({ id: o.id, text: o.text.trim() })),
            correctOptionId: q.correctOptionId,
            explanation: q.explanation.trim() ? q.explanation.trim() : null,
          }),
        ),
      };
      return target.kind === 'final'
        ? saveCourseFinalQuiz(courseId, doc)
        : saveLessonQuiz(target.lesson.id, doc);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(describeTrainingError(err, 'Could not save the quiz.'));
    },
  });

  function patchQuestion(index: number, patch: Partial<QuestionDraft>): void {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  }

  const valid =
    title.trim().length > 0 &&
    questions.length > 0 &&
    questions.every(
      (q) =>
        q.prompt.trim().length > 0 &&
        q.options.length >= 2 &&
        q.options.every((o) => o.text.trim().length > 0) &&
        q.options.some((o) => o.id === q.correctOptionId),
    );

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={target.kind === 'final' ? 'Final quiz' : `Quiz — ${target.lesson.title}`}
      size="lg"
      className="max-h-[90vh] overflow-y-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_120px_120px]">
          <Input
            label="Quiz title"
            name="quiz-title"
            required
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
          <Input
            label="Pass %"
            name="quiz-pass-threshold"
            type="number"
            min={0}
            max={100}
            required
            value={passThreshold}
            onChange={(e) => {
              setPassThreshold(Number(e.target.value));
            }}
          />
          <Input
            label="Max attempts"
            name="quiz-max-attempts"
            type="number"
            min={1}
            max={100}
            placeholder="∞"
            value={maxAttempts}
            onChange={(e) => {
              setMaxAttempts(e.target.value);
            }}
            hint="Blank = unlimited"
          />
        </div>

        {questions.map((question, qIndex) => (
          <fieldset key={question.key} className="space-y-2 rounded-kw-md border border-kw-border p-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  label={`Question ${String(qIndex + 1)}`}
                  name={`quiz-q-${question.key}`}
                  required
                  value={question.prompt}
                  onChange={(e) => {
                    patchQuestion(qIndex, { prompt: e.target.value });
                  }}
                  placeholder="Ask the question…"
                />
              </div>
              <button
                type="button"
                aria-label={`Remove question ${String(qIndex + 1)}`}
                disabled={questions.length === 1}
                onClick={() => {
                  setQuestions((prev) => prev.filter((_, i) => i !== qIndex));
                }}
                className="mt-7 rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:opacity-30"
              >
                <Trash2 className="size-4" />
              </button>
            </div>

            <div className="space-y-1.5">
              {question.options.map((option, oIndex) => (
                <div key={option.id} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`quiz-correct-${question.key}`}
                    aria-label={`Mark option ${String(oIndex + 1)} of question ${String(qIndex + 1)} correct`}
                    checked={question.correctOptionId === option.id}
                    onChange={() => {
                      patchQuestion(qIndex, { correctOptionId: option.id });
                    }}
                    className="size-4 shrink-0 border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
                  />
                  <input
                    type="text"
                    aria-label={`Option ${String(oIndex + 1)} of question ${String(qIndex + 1)}`}
                    value={option.text}
                    onChange={(e) => {
                      patchQuestion(qIndex, {
                        options: question.options.map((o, i) =>
                          i === oIndex ? { ...o, text: e.target.value } : o,
                        ),
                      });
                    }}
                    placeholder={`Option ${String(oIndex + 1)}`}
                    className="min-w-0 flex-1 rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                  />
                  <button
                    type="button"
                    aria-label={`Remove option ${String(oIndex + 1)} of question ${String(qIndex + 1)}`}
                    disabled={question.options.length <= 2}
                    onClick={() => {
                      const remaining = question.options.filter((_, i) => i !== oIndex);
                      patchQuestion(qIndex, {
                        options: remaining,
                        correctOptionId:
                          question.correctOptionId === option.id
                            ? (remaining[0]?.id ?? '')
                            : question.correctOptionId,
                      });
                    }}
                    className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:opacity-30"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  patchQuestion(qIndex, {
                    options: [...question.options, { id: genId('opt'), text: '' }],
                  });
                }}
                className="text-[12px] font-medium text-kw-cerulean hover:underline"
              >
                + Add option
              </button>
            </div>

            <Input
              label="Explanation (optional, shown after grading)"
              name={`quiz-expl-${question.key}`}
              value={question.explanation}
              onChange={(e) => {
                patchQuestion(qIndex, { explanation: e.target.value });
              }}
            />
            <p className="text-[11px] text-kw-muted">The selected radio marks the correct answer.</p>
          </fieldset>
        ))}

        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            setQuestions((prev) => [...prev, newQuestion()]);
          }}
        >
          <Plus className="size-3.5" />
          Add question
        </Button>

        {error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!valid}>
            Save quiz
          </Button>
        </div>
      </form>
    </Modal>
  );
}
